// WebUI #167 (the line drawer, lead decision 2026-10-12). No Feature/Test ID is allocated in this file: the IDs for this
// behaviour are pending define-feature-test (see the MR). Tests live in LedgerQueueSections.test.js,
// LedgerQueueSections.moves.test.js and ledger/moneyActionSod.test.js.
//
// The drawer: the Details section that sits on top of the item's drawer
// (TxHistoryDrawer `details` slot). It shows the item, and ONE Edit form for it: Title, Amount and one Reason. Save sends only
// the changed fields, through the parent's `onSave` (LedgerQueueSections owns the Ledger writes, the step-up and the
// partial-failure message). This component never calls the API itself.
//
// Rules rendered here:
//  * a written draft line (WRITTEN_STAGES) is LOCKED: no Edit, a sentence says why;
//  * an Advice item's title is disabled ("Add it to the invoice to edit the title.": Core owns it until the TXN is minted, owner 10-08);
//  * an own-account customer (the viewer): the title may be edited (canEditText), the amount is not: it shows
//    "Another billing manager must change this amount." (maker-checker, ML001-F0239);
//  * Save is disabled until something changed AND a reason is typed.
import React, { useEffect, useState } from 'react';
import { parseAmount } from './MoveControls';

const TITLE_MAX = 500;
const REASON_MAX = 500;

export default function ItemDetails({
  kind, item, stageText = '', dateText = '', txText = '', canEdit = false, canEditText = false, locked = false, lockText = '', noEditText = '', startInEdit = false,
  money, draftTotal = null, audit = [], sessionEdits = [], message = null, busy = false, onSave,
}) {
  const isDraft = kind === 'draft';
  const [editing, setEditing] = useState(startInEdit === true && !locked && (isDraft ? (canEdit || canEditText) : canEdit));
  const [title, setTitle] = useState(item.description || '');
  const [amount, setAmount] = useState(Number.isFinite(item.amount) ? item.amount.toFixed(2) : '');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');

  const titleEditable = isDraft && !locked && (canEdit || canEditText);
  const amountEditable = !locked && canEdit;
  const canStartEdit = !locked && (canEdit || (isDraft && canEditText));
  const ownAccountAmount = !locked && !canEdit && canEditText;

  const titleDirty = titleEditable && editing && title.trim() !== (item.description || '').trim();
  const amountDirty = amountEditable && editing && amount !== (Number.isFinite(item.amount) ? item.amount.toFixed(2) : '');
  const newPounds = amountDirty ? parseAmount(amount) : null;
  const canSave = !busy && (titleDirty || amountDirty) && reason.trim() !== '';

  const start = () => {
    setTitle(item.description || '');
    setAmount(Number.isFinite(item.amount) ? item.amount.toFixed(2) : '');
    setReason('');
    setProblem('');
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setProblem(''); setReason(''); };

  const submit = async (event) => {
    event.preventDefault();
    const text = title.trim();
    const why = reason.trim();
    if (titleDirty && !text) { setProblem('Enter a description for the line.'); return; }
    if (titleDirty && text.length > TITLE_MAX) { setProblem('The description must be 500 characters or fewer.'); return; }
    if (amountDirty && newPounds == null) { setProblem('Enter an amount in pounds, for example 45.00.'); return; }
    if (!why) { setProblem('A reason is required.'); return; }
    if (why.length > REASON_MAX) { setProblem('The reason must be 500 characters or fewer.'); return; }
    if (!titleDirty && !amountDirty) return;
    setProblem('');
    await onSave({ title: titleDirty ? text : undefined, amount: amountDirty ? newPounds : undefined, reason: why });
  };

  // The form closes only on a saved message; a partial or full failure keeps it open with the values typed.
  useEffect(() => { if (message && message.tone === 'ok') setEditing(false); }, [message]);

  const shownAmount = money ? money(item.amount, item.currency) : String(item.amount);
  const deltaText = amountDirty && newPounds != null && money
    ? `was ${money(item.amount, item.currency)} → now ${money(newPounds, item.currency)}`
    : '';
  const newTotal = deltaText && isDraft && Number.isFinite(draftTotal) && money
    ? money(Math.round((draftTotal + newPounds - item.amount) * 100) / 100, item.currency)
    : '';

  return <section className="tx-section item-details" aria-label="Details" data-testid="item-details">
    <h4>Details</h4>
    {!editing ? <>
      <dl className="item-details-fields">
        <dt>Title</dt><dd data-testid="item-title">{item.description}</dd>
        {dateText ? <><dt>Date</dt><dd>{dateText}</dd></> : null}
        <dt>Amount</dt><dd data-testid="item-amount">{shownAmount}</dd>
        {txText ? <><dt>TXN</dt><dd data-testid="item-txn">{txText}</dd></> : null}
        {stageText ? <><dt>Stage</dt><dd data-testid="item-stage">{stageText}</dd></> : null}
      </dl>
      {locked ? <p className="ledger-queue-muted" role="status" data-testid="item-locked">{lockText}</p> : null}
      {!locked && !canStartEdit && noEditText ? <p className="ledger-queue-muted" role="status" data-testid="item-no-edit">{noEditText}</p> : null}
      {canStartEdit ? <button type="button" className="ledger-queue-secondary" data-testid="item-edit" disabled={busy} onClick={start}>Edit</button> : null}
    </> : <form className="ledger-queue-form" data-testid="item-edit-form" onSubmit={submit} noValidate>
      <label>Title
        <input value={title} disabled={!titleEditable} maxLength={TITLE_MAX} onChange={(event) => setTitle(event.target.value)} />
      </label>
      {!titleEditable ? <span className="ledger-queue-muted" data-testid="item-title-hint">Add it to the invoice to edit the title.</span> : null}
      <label>Amount (£)
        <input inputMode="decimal" value={amount} disabled={!amountEditable} onChange={(event) => setAmount(event.target.value)} />
      </label>
      {ownAccountAmount ? <span className="ledger-queue-muted" data-testid="item-amount-hint">Another billing manager must change this amount.</span> : null}
      {deltaText ? <span className="ledger-queue-muted" data-testid="item-amount-delta">{deltaText}{newTotal ? `. New invoice total: ${newTotal}` : ''}</span> : null}
      <label>Reason (required)
        <input required maxLength={REASON_MAX} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. agreed 10% off" />
      </label>
      {problem ? <span className="ledger-account-error" role="alert">{problem}</span> : null}
      <button type="submit" className="ledger-queue-primary" data-testid="item-save" disabled={!canSave}>Save</button>
      <button type="button" onClick={cancel}>Cancel</button>
    </form>}
    {message ? <p className={message.tone === 'error' ? 'ledger-account-error' : 'ledger-queue-muted'} role={message.tone === 'error' ? 'alert' : 'status'} data-testid="item-message">{message.text}</p> : null}
    <h4>Edit history</h4>
    <ul className="item-details-history" data-testid="item-history">
      {audit.map((note) => <li key={`audit-${note}`} data-testid="item-history-audit">{note}</li>)}
      {sessionEdits.map((entry) => <li key={entry.id} data-testid="item-history-line">{`${entry.at} · ${entry.field}: ${entry.from} → ${entry.to} · reason: ${entry.reason} · by ${entry.by}`}</li>)}
      {!audit.length && !sessionEdits.length ? <li className="ledger-queue-muted">No changes yet.</li> : null}
    </ul>
  </section>;
}
